/**
 * Sets `--dl-*` CSS custom properties on a scope element from a theme's
 * tokens, so themed components (Section, CTAButton) read color values
 * at render time. Mounting the injector at the editor preview root or
 * the live page root scopes the theme without polluting :root globally.
 *
 * Also handles font tokens: writes `--dl-font-display`, `--dl-font-body`,
 * `--dl-font-mono` CSS vars, and dynamically loads the matching Google
 * Fonts stylesheet. The legacy `font.sans` token is preserved for
 * backward compat (maps to `--dl-font-body` if `body` is unset).
 */

import { useEffect } from "react";

import { fontFamilyForKey, googleFontsUrl, type FontKey } from "./fonts";

export type ThemeTokens = {
  colors?: {
    // Brand pair
    brand?: string;
    brandHover?: string;
    onBrand?: string;
    // Accent pair
    accent?: string;
    onAccent?: string;
    // Surface pair (page bg + its text)
    surface?: string;
    onSurface?: string;
    // Contrast pair (dark sections + their text)
    contrast?: string;
    onContrast?: string;
    // State group (single tokens — text color contrast is implicit)
    success?: string;
    warning?: string;
    danger?: string;
  };
  font?: {
    /** Display / serif / heading face — keyed font from FONTS catalog. */
    display?: FontKey;
    /** Body / sans face — keyed font from FONTS catalog. */
    body?: FontKey;
    /** Mono / accent face — keyed font from FONTS catalog. */
    mono?: FontKey;
    /** Legacy free-form sans stack. Used as fallback for `body`. */
    sans?: string;
  };
  /**
   * Design-level style tokens. These drive *form* (not just color/type) so
   * the gallery can offer genuinely distinct styles — glassmorphism, bento,
   * brutalist, soft, big-type, editorial — from the same component set.
   *
   * BACKWARD-COMPAT: every var emitted here has a CSS fallback in the
   * components, and the injector only emits design vars when `design` is
   * present. A theme without a `design` group renders exactly as before.
   */
  design?: {
    /** Border-radius scale. e.g. "0px" brutalist, "8px" default, "20px" soft/bento, "9999px" pill. */
    radius?: string;
    /** Card/elevation treatment. none = flat; soft = layered blur; hard = offset solid (brutalist). */
    shadow?: "none" | "soft" | "hard";
    /** Surface fill. flat = solid; glass = translucent + backdrop blur; gradient = themed gradient. */
    surface?: "flat" | "glass" | "gradient";
    /** Heading size multiplier. 1 = default, 1.25 = big-type/editorial. */
    fontScale?: number;
    /** Border treatment for bordered elements. */
    border?: "hairline" | "bold" | "none";
  };
};

type Props = {
  tokens: ThemeTokens | null | undefined;
  /** Element to scope variables on. Defaults to <html>. */
  scope?: HTMLElement | null;
};

const VAR_MAP: Array<[keyof NonNullable<ThemeTokens["colors"]>, string]> = [
  ["brand", "--dl-brand"],
  ["brandHover", "--dl-brand-hover"],
  ["onBrand", "--dl-on-brand"],
  ["accent", "--dl-accent"],
  ["onAccent", "--dl-on-accent"],
  ["surface", "--dl-surface"],
  ["onSurface", "--dl-on-surface"],
  ["contrast", "--dl-contrast"],
  ["onContrast", "--dl-on-contrast"],
  ["success", "--dl-success"],
  ["warning", "--dl-warning"],
  ["danger", "--dl-danger"],
];

/** Default font keys — kept in sync with the editorial direction. */
const DEFAULT_DISPLAY: FontKey = "fraunces";
const DEFAULT_BODY: FontKey = "dm-sans";
const DEFAULT_MONO: FontKey = "jetbrains-mono";

export function ThemeStyleInjector({ tokens, scope }: Props) {
  useEffect(() => {
    // v2 difference: scope is REQUIRED. The studio shell has its own
    // cream/ink theme; the `--dl-*` page-design vars must stay confined to
    // the preview canvas and never leak onto <html>.
    const target = scope;
    if (!target) return;
    const colors = tokens?.colors ?? {};
    const written: string[] = [];
    for (const [key, cssVar] of VAR_MAP) {
      const v = colors[key];
      if (typeof v === "string" && v) {
        target.style.setProperty(cssVar, v);
        written.push(cssVar);
      }
    }

    // Font tokens — write CSS vars even when defaulting, so components see
    // a consistent stack regardless of how the page was reached.
    const display = tokens?.font?.display ?? DEFAULT_DISPLAY;
    const body = tokens?.font?.body ?? DEFAULT_BODY;
    const mono = tokens?.font?.mono ?? DEFAULT_MONO;

    target.style.setProperty("--dl-font-display", fontFamilyForKey(display, "display"));
    target.style.setProperty("--dl-font-body", fontFamilyForKey(body, "body"));
    target.style.setProperty("--dl-font-mono", fontFamilyForKey(mono, "mono"));
    written.push("--dl-font-display", "--dl-font-body", "--dl-font-mono");

    // Backward-compat legacy free-form sans stack.
    if (tokens?.font?.sans) {
      target.style.setProperty("--dl-font-sans", tokens.font.sans);
      written.push("--dl-font-sans");
    }

    // Design-level style tokens. Only emitted when a `design` group exists so
    // existing themes are untouched. Each emitted var still has a component
    // fallback, so partial design groups degrade gracefully too.
    const design = tokens?.design;
    if (design) {
      // radius
      target.style.setProperty("--dl-radius", design.radius ?? "12px");
      written.push("--dl-radius");

      // shadow — full box-shadow strings keyed off the theme's own colors.
      const shadowMap: Record<NonNullable<typeof design.shadow>, string> = {
        none: "none",
        soft:
          "0 1px 2px color-mix(in srgb, var(--dl-on-surface, #0a0a0a) 7%, transparent), " +
          "0 16px 40px -16px color-mix(in srgb, var(--dl-on-surface, #0a0a0a) 24%, transparent)",
        hard: "6px 6px 0 0 var(--dl-contrast, #0a0a0a)",
      };
      target.style.setProperty("--dl-shadow", shadowMap[design.shadow ?? "soft"]);
      written.push("--dl-shadow");

      // surface — flat | glass | gradient. Glass emits a translucent fill plus
      // a blur radius; gradient emits a themed linear-gradient.
      const surface = design.surface ?? "flat";
      target.style.setProperty("--dl-surface-style", surface);
      written.push("--dl-surface-style");
      if (surface === "glass") {
        target.style.setProperty(
          "--dl-surface-bg",
          "color-mix(in srgb, var(--dl-surface, #ffffff) 55%, transparent)",
        );
        target.style.setProperty("--dl-surface-blur", "14px");
        written.push("--dl-surface-bg", "--dl-surface-blur");
        // BUTTON tokens — a glass surface must make CTAs glassy too (not just
        // backgrounds). A translucent brand tint kept ≥80% opaque so on-brand
        // label text stays WCAG-legible over varied backdrops; a light hairline
        // + inset sheen + backdrop blur read as frosted glass. Consumed by
        // CTAButton / Hero / Pricing with a solid-brand fallback.
        target.style.setProperty(
          "--dl-btn-bg",
          "color-mix(in srgb, var(--dl-brand, #2563eb) 82%, transparent)",
        );
        target.style.setProperty("--dl-btn-fg", "var(--dl-on-brand, #ffffff)");
        target.style.setProperty("--dl-btn-blur", "12px");
        target.style.setProperty(
          "--dl-btn-border",
          "1px solid color-mix(in srgb, #ffffff 38%, transparent)",
        );
        target.style.setProperty(
          "--dl-btn-shadow",
          "inset 0 1px 0 0 color-mix(in srgb, #ffffff 45%, transparent), " +
            "0 10px 30px -14px color-mix(in srgb, var(--dl-brand, #2563eb) 55%, transparent)",
        );
        written.push("--dl-btn-bg", "--dl-btn-fg", "--dl-btn-blur", "--dl-btn-border", "--dl-btn-shadow");
      } else if (surface === "gradient") {
        target.style.setProperty(
          "--dl-surface-gradient",
          "linear-gradient(135deg, " +
            "color-mix(in srgb, var(--dl-brand, #2563eb) 14%, var(--dl-surface, #ffffff)) 0%, " +
            "var(--dl-surface, #ffffff) 100%)",
        );
        written.push("--dl-surface-gradient");
        // BUTTON tokens — a gradient surface makes CTAs a brand→accent gradient
        // (opaque, so contrast is governed by --dl-on-brand as usual), with a
        // soft accent-tinted glow. No blur (gradient isn't translucent).
        target.style.setProperty(
          "--dl-btn-bg",
          "linear-gradient(135deg, var(--dl-brand, #2563eb) 0%, var(--dl-accent, #7c3aed) 100%)",
        );
        target.style.setProperty("--dl-btn-fg", "var(--dl-on-brand, #ffffff)");
        target.style.setProperty("--dl-btn-border", "1px solid transparent");
        target.style.setProperty(
          "--dl-btn-shadow",
          "0 12px 30px -12px color-mix(in srgb, var(--dl-accent, #7c3aed) 55%, transparent)",
        );
        written.push("--dl-btn-bg", "--dl-btn-fg", "--dl-btn-border", "--dl-btn-shadow");
      }

      // fontScale — heading size multiplier.
      target.style.setProperty("--dl-font-scale", String(design.fontScale ?? 1));
      written.push("--dl-font-scale");

      // border — width + color pair for bordered elements.
      const borderMap: Record<
        NonNullable<typeof design.border>,
        { width: string; color: string }
      > = {
        none: { width: "0px", color: "transparent" },
        hairline: {
          width: "1px",
          color: "color-mix(in srgb, var(--dl-on-surface, #0a0a0a) 10%, transparent)",
        },
        bold: { width: "2px", color: "var(--dl-contrast, #0a0a0a)" },
      };
      const b = borderMap[design.border ?? "hairline"];
      target.style.setProperty("--dl-border-width", b.width);
      target.style.setProperty("--dl-border-color", b.color);
      written.push("--dl-border-width", "--dl-border-color");
    }

    // Inject (or update) the Google Fonts stylesheet that covers all three
    // chosen faces. One <link> per page; href changes when fonts change.
    const fontsHref = googleFontsUrl([display, body, mono]);
    let link = document.querySelector<HTMLLinkElement>("link[data-dl-fonts]");
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.setAttribute("data-dl-fonts", "1");
      document.head.appendChild(link);
    }
    if (link.href !== fontsHref) link.href = fontsHref;

    return () => {
      for (const v of written) target.style.removeProperty(v);
      // Note: we deliberately leave the <link> in the DOM so navigations
      // between pages don't flash unstyled text while the next theme
      // loads. The href will update on next mount if fonts change.
    };
  }, [tokens, scope]);

  return null;
}
